import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { GetServerSidePropsContext } from 'next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { useAppContext } from '@context/AppContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AuthForm } from '@components/Form/styles';
import Preloader, { SIZES } from '@components/Preloader';
import WarningIcon from '@assets/images/icons/warning.svg';
import { GlobalError, GlobalErrorIcon } from '@styles/globalStyles';

import Input, { INPUT_TYPES } from '@components/Form/Input';
import Button, { BUTTON_TYPE } from '@components/Button';
import Link from '@components/Link';
import routes from '@utils/routes';
import { API, APIRoutes, setApiAuthHeaders } from '@utils/api';

function SignInPage() {
  const { setUser } = useAppContext();
  const [authError, setAuthError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const { t } = useTranslation(['login', 'common']);
  const { query, replace } = useRouter();

  useEffect(() => {
    if (query.confirmed === 'true') {
      toast.success(t('Account has been confirmed! You can sign in now', { ns: 'common' }));
      replace(routes.signin, undefined, { shallow: true });
    }
  }, [query, replace, t]);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(t('email regex')).required(t('email required')),
    password: Yup.string().required(t('password required')),
  });

  const formOptions = { resolver: yupResolver(validationSchema) };
  const { handleSubmit, formState, control } = useForm(formOptions);
  const router = useRouter();

  const { errors } = formState;

  const { mutate: signin, isLoading } = useMutation((data) => API.post(APIRoutes.auth.signin, { user: data }));

  const onSubmit = (formData) => {
    signin(formData, {
      onError: ({ response: { data } }) => {
        setAuthError(data.error);
        toast.error(data.error);
      },
      onSuccess: ({ data, headers }) => {
        const { user } = data;
        setRedirecting(true);
        setApiAuthHeaders(headers.authorization);
        setUser(user);
        const { rodo, termsAndConditions } = user || {};
        if (!(rodo && termsAndConditions)) {
          router.replace({ pathname: routes.acceptTerms });
        } else {
          router.back();
        }
        toast.success(t('Welcome {{name}}. You are logged in.', { name: data.user.fullName }));
      },
    });
  };

  return (
    <>
      <Head>
        <title>{t('Sign in')}</title>
        <meta property="og:title" content="Sign in" />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}/images/lodz.png`} />
        <meta property="og:image:secure_url" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}/images/lodz.png`} />
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="279" />
        <meta property="og:description" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}/logowanie`} />
      </Head>

      <AuthForm centered onSubmit={handleSubmit(onSubmit)}>
        {isLoading || redirecting ? <Preloader size={SIZES.LARGE} overlay /> : null}
        {authError ? (
          <GlobalError>
            <GlobalErrorIcon>
              <Image src={WarningIcon} alt="" />
            </GlobalErrorIcon>
            {authError}
          </GlobalError>
        ) : null}
        <Controller
          name="email"
          defaultValue=""
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <Input
              name={name}
              inputType={INPUT_TYPES.TEXT}
              onChange={onChange}
              value={value}
              label={t('email')}
              error={authError ? { message: '' } : errors.email}
            />
          )}
        />

        <Controller
          name="password"
          defaultValue=""
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <Input
              autoComplete="off"
              name={name}
              inputType={INPUT_TYPES.PASSWORD}
              onChange={onChange}
              value={value}
              label={t('password')}
              error={authError ? { message: '' } : errors.password}
            />
          )}
        />

        <Link href={routes.passwordReset} replace>
          {t('remind password')}
        </Link>
        <Button type={BUTTON_TYPE.SUBMIT} isLoading={isLoading}>
          {t('button')}
        </Button>
        <span>
          {t('question')} &nbsp;
          <Link href={routes.signup} replace>
            {t('signup')}
          </Link>
        </span>
      </AuthForm>
    </>
  );
}

export async function getServerSideProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['login', 'common'])),
    },
  };
}

export default SignInPage;
